import { AxiosResponse } from "axios";
import { Dispatch } from "@reduxjs/toolkit";

import {
  ImportBatchFormValues,
  ProductTokenizationDraft,
  ProductTokenizeFormValues,
} from "./types";
import {
  showAlert,
  isAPISuccessful,
  checkInternetAndShowAlert,
} from "../../../common/utils";
import {
  Env,
  Module,
  EndPoint,
  putApiCall,
  getApiCall,
  postApiCall,
  deleteApiCall,
} from "../../../common/constants";
import {
  AnalyticsEvents,
  capturePosthogEvent,
} from "../../../common/utils/analytics";
import {
  setDraftDetail,
  setBatchDetail,
  setBatchListing,
  setStrainListing,
  setSubTypeListing,
  setCategoryListing,
  setRatingListingData,
  setProductTypeListing,
  setHeaderNameListing,
  setLicenseNameListing,
  setUploadBatchListing,
  setBlockChainDetails,
  setConsumerListingData,
  setConsumerBatchDetails,
  setTokenizedBatchListing,
  setAssociatedStrainListing,
  setRatingBatchDetailsProps,
  setProductCollectedListing,
} from "./slices";
import {
  getTimezone,
  apiSuccessCode,
} from "../../../common/constants/axios.instance";
import { RootState } from "../../../redux/store";
import { setLoading } from "../../../redux/global.slice";
import { SetSubmittingType } from "../../../redux/global.types";
import { TXN_MODE, TXN_STATUS, TXN_TYPE } from "../../../common/constants/enum";

export const extractFileHeadersName =
  (values: any, callback?: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.extractFileHeaders,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          dispatch(setHeaderNameListing(data?.data?.data));
          if (callback) callback();
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const uploadBatch =
  (values: any, callback?: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.uploadBatchData,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.postApiSuccess) {
          //   dispatch(setHeaderNameListing(data?.data?.data));
          if (callback) callback();
          showAlert(2, data?.message);
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const getUploadedBatchData =
  () => (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    const {
      uploadBatchListingQueryParameters: { pageNo, limit },
    } = getState().batches;

    const params = `?pageNo=${pageNo}&limit=${limit}`;

    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer + EndPoint.uploadedBatchList,
      params,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          dispatch(setUploadBatchListing(data?.data));
        } else if (data.error)
          showAlert(
            2,
            "We're experiencing technical difficulties. Please try again later.",
          );
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const finalizeUploadBatch =
  (callback?: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.finalizeBatchUpload,
      {},
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.postApiSuccess) {
          // dispatch(setHeaderNameListing(data?.data?.data));
          showAlert(2, data?.message);
          if (callback) callback();
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const getBatchListing =
  (type: string) => (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    const {
      batchFilter: { productType, subType, coaTokenize, status },
      batchListingQueryParameters: { pageNo, limit, searchKey },
    } = getState().batches;

    const params = [
      `pageNo=${pageNo}`,
      `limit=${limit}`,
      `type=${type}`,
      coaTokenize.length !== 0 ? `&coaTokenization=${coaTokenize}` : "",
      searchKey.trim() !== ""
        ? `searchKey=${encodeURIComponent(searchKey.trim())}`
        : "",
      productType.length !== 0 ? `&productTypeIds=${productType}` : "",
      subType.length !== 0 ? `&productSubTypeIds=${subType}` : "",
      status.length !== 0 ? `&isArchived=true` : "",
    ]
      .filter((param) => param) // Filter out empty strings
      .join("&");

    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer,
      `${EndPoint.batches}?${params}`,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          if (type === "tokenized") {
            dispatch(setTokenizedBatchListing(data?.data));
          } else dispatch(setBatchListing(data?.data));
        } else if (data.error)
          showAlert(
            2,
            "We're experiencing technical difficulties. Please try again later.",
          );
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const getBatchDetails =
  (batchId: string, isLoaderClosed?: boolean) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    getApiCall(
      `${Module.manufacturer}${EndPoint.batches}/${batchId}`,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data.statusCode === apiSuccessCode.success) {
          dispatch(setBatchDetail(data.data));
        } else if (data.error) showAlert(2, data.message);
        if (!isLoaderClosed) dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const getLicenseListing =
  () => (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer + EndPoint.licenseListing,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          dispatch(setLicenseNameListing(data?.data));
        } else if (data.error)
          showAlert(
            2,
            "We're experiencing technical difficulties. Please try again later.",
          );
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const onImportBatch =
  (
    values: ImportBatchFormValues,
    setSubmitting: SetSubmittingType,
    callback?: (isStrain: boolean) => void,
    errorCallback?: () => void,
  ) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    setSubmitting(true);
    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.importBatch,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (
          data?.statusCode === apiSuccessCode.ok ||
          data?.statusCode === apiSuccessCode.success ||
          data?.statusCode === apiSuccessCode.postApiSuccess
        ) {
          const batchId = data?.data?.batchId;
          const isStrain = data?.data?.isStrain;

          showAlert(1, data.message);
          if (callback) callback(isStrain);
          if (batchId) localStorage.setItem("importedBatchId", batchId);
        }

        setSubmitting(false);
        dispatch(setLoading(false));
      },
      (error: any) => {
        setSubmitting(false);
        dispatch(setLoading(false));
        showAlert(2, error.message);
        if (errorCallback) errorCallback();
      },
    );
  };

export const addCoa = (
  values: any,
  setSubmitting: SetSubmittingType,
  callback?: Function | any,
) => {
  return (dispatch: any) => {
    setSubmitting(true);
    dispatch(setLoading(true));

    putApiCall(
      `${Module.manufacturer}${EndPoint.batches}`,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          showAlert(1, data.message);
          if (callback) {
            callback();
          }
        } else {
          dispatch(setLoading(false));
        }
        setSubmitting(false);
      },
      (error: any) => {
        setSubmitting(false);
        showAlert(2, error.message);
        dispatch(setLoading(false));
      },
    );
  };
};

export const fetchLiims =
  (values: { batchId: string }, callback?: (url: string) => void) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));
    postApiCall(
      Module.manufacturer + EndPoint.fetchLiims,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          const url = data?.data?.coaPdfUrl;
          if (callback) callback(url);
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const getCategoryListing =
  () => (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer + EndPoint.fetchCatergorySubCategory,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          dispatch(setCategoryListing(data?.data));
        } else if (data.error)
          showAlert(
            2,
            "We're experiencing technical difficulties. Please try again later.",
          );
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const onAssociateStrainBatch = (
  payload: {
    type: string;
    batchMongoId: string;
    strainMongoId: string;
  },
  callback?: () => void, // Ensure callback is a function
) => {
  return (dispatch: any) => {
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    putApiCall(
      `${Module.manufacturer}${EndPoint.associateStrain}`,
      payload,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.ok) {
          showAlert(1, data.message);

          if (callback) {
            callback(); // Ensure callback is executed properly
          }
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        showAlert(2, error.message);
        dispatch(setLoading(false));
      },
    );
  };
};

export const getAssociatedStrainListing =
  (callback?: () => void) =>
  (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    const {
      associatedStrainListingQueryParameters: { searchKey },
    } = getState().batches;

    const params = [
      searchKey.trim() !== ""
        ? `searchKey=${encodeURIComponent(searchKey.trim())}`
        : "",
    ]
      .filter((param) => param) // Filter out empty strings
      .join("&");

    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer,
      `${EndPoint.associatedStrainList}?${params}`,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          dispatch(setAssociatedStrainListing(data?.data));
          if (callback) callback();
        } else if (data.error)
          showAlert(
            2,
            "We're experiencing technical difficulties. Please try again later.",
          );
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const onIpfsUpload =
  (
    values: { batchMongoId: string },
    callback?: (metaDataIpfsHash: string) => void,
  ) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));
    postApiCall(
      Module.manufacturer + EndPoint.ipfsUpload,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          const metaDataIpfsHash = data?.data?.metaDataIpfsHash;
          if (callback) callback(metaDataIpfsHash);
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const onTokenizeCOAProduct =
  (
    values: {
      type: TXN_TYPE;
      submittedBy: `0x${string}`;
      fromAddress: `0x${string}`;
      toAddress: any;
      status: TXN_STATUS;
      userOperationHash: string;
      transactionMode: TXN_MODE;
      batchMongoId: string;
      isProductTokenized?: boolean;
    },
    strainId: string,
    callback?: (params: string) => void,
  ) =>
  (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    const { isProductTokenized, ...rest } = values;
    dispatch(setLoading(true));
    postApiCall(
      Module.manufacturer +
        `${values.isProductTokenized ? EndPoint.addProductTokenizationTransaction : EndPoint.addBlockchainTransaction}`,
      rest,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          const uid = getState().verifyEmail.userId;
          const batchId = values.batchMongoId;
          if (values.isProductTokenized) {
            capturePosthogEvent(AnalyticsEvents.BATCH_TOKENIZED, {
              user_id: uid,
              batch_id: batchId,
              strain_id: strainId ?? "",
            });
          } else {
            capturePosthogEvent(AnalyticsEvents.COA_TOKENIZED, {
              user_id: uid,
              batch_id: batchId,
              strain_id: strainId ?? "",
            });
          }

          const coaData = data?.data?.coaNftViewUrl;
          const productData = data?.data?.viewProductOnChainUrl;

          if (callback) {
            const params = values.isProductTokenized ? productData : coaData;
            callback(params);
          }
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const updateProductTokenizationDetails = (
  values: ProductTokenizeFormValues,
  callback?: Function | any,
) => {
  return (dispatch: any) => {
    dispatch(setLoading(true));

    const { batchMongoId, totalProductCount, qrCodePurchasePackets } = values;

    const dataToSend = {
      batchMongoId,
      totalProductCount,
      qrCodePurchasePackets,
    };

    postApiCall(
      `${Module.manufacturer}${EndPoint.productTokenization}`,
      dataToSend,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          if (callback) {
            callback();
          }
        } else dispatch(setLoading(false));
      },
      (error: any) => {
        showAlert(2, error.message);
        dispatch(setLoading(false));
      },
    );
  };
};

export const saveProductTokenizationDraft = (
  values: any,
  isSilent: boolean = false,
  callback?: Function | any,
) => {
  return (dispatch: any) => {
    if (!isSilent) {
      dispatch(setLoading(true));
    }

    putApiCall(
      `${Module.manufacturer}${EndPoint.productTokenizationDraft}`,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          if (callback) {
            callback();
          }
        } else if (callback) {
          callback();
        }
        if (!isSilent) {
          dispatch(setLoading(false));
        }
      },
      (error: any) => {
        if (callback) {
          callback();
        }
        if (!isSilent) {
          showAlert(2, error.message);
          dispatch(setLoading(false));
        }
      },
    );
  };
};

export const saveProductTokenizationDraftKeepAlive = (
  payload: Record<string, any>,
  token: string,
) => {
  const url = `${Env.BASE_URL}${Module.manufacturer}${EndPoint.productTokenizationDraft}`;
  const headers: HeadersInit = {
    platform: "1",
    "accept-language": "en",
    "X-API-Key": Env.API_KEY || "",
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    timezone: new Date().valueOf().toString(),
    offset: Math.abs(new Date().getTimezoneOffset() * 60 * 1000).toString(),
  };

  try {
    fetch(url, {
      method: "PUT",
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (e) {
    console.error("Failed to save draft on unload:", e);
  }
};

export const getProductTokenizationDraft =
  (
    batchMongoId: string,
    callback?: (draft: ProductTokenizationDraft) => void,
  ) =>
  (dispatch: Dispatch<any>) => {
    if (!checkInternetAndShowAlert(showAlert)) return;

    getApiCall(
      `${Module.manufacturer}${EndPoint.productTokenizationDraft}/${batchMongoId}`,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode) && data?.data) {
          dispatch(setDraftDetail(data.data));
          if (callback) callback(data.data);
        } else {
          // No draft saved yet — clear any stale draft from state
          dispatch(setDraftDetail(null));
        }
      },
      () => {
        // Silently fail — a missing draft is not an error worth surfacing
        dispatch(setDraftDetail(null));
      },
    );
  };

export const deleteSavedDraft = (
  batchMongoId: string,
  callback?: () => void,
) => {
  return (dispatch: Dispatch<any>) => {
    deleteApiCall(
      `${Module.manufacturer}${EndPoint.productTokenizationDraft}/${batchMongoId}`,
      "",

      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data.statusCode === apiSuccessCode.success) {
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        showAlert(2, error.message);
        dispatch(setLoading(false));
      },
    );
  };
};

export const printOnDemandGenerate = (
  values: ProductTokenizeFormValues & { internalNote?: string },
  callback: () => void,
) => {
  return (dispatch: any) => {
    dispatch(setLoading(true));

    const { fringeLabel, totalLabels, batchMongoId, internalNote } = values;

    const dataToSend = {
      batchId: batchMongoId,
      fringeLabel: Number(fringeLabel || 0),
      totalLabels: Number(totalLabels),
      internalNote: internalNote || "",
    };

    postApiCall(
      `${Module.manufacturer}${EndPoint.printOnDemand}`,
      dataToSend,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;

        if (isAPISuccessful(data?.statusCode)) {
          dispatch(setLoading(false));
          if (callback) {
            callback();
          }
        } else {
          dispatch(setLoading(false));
        }
      },
      (error: any) => {
        showAlert(2, error.message);
        dispatch(setLoading(false));
      },
    );
  };
};

export const addPrintOnDemandLabels = (
  values: {
    batchId: string;
    totalLabels: number;
    fringeLabel: number;
    internalNote?: string;
  },
  callback?: (data?: any) => void,
  options?: { keepLoading?: boolean },
) => {
  return (dispatch: any) => {
    dispatch(setLoading(true));

    postApiCall(
      `${Module.manufacturer}/print-on-demand/add-labels`,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (!options?.keepLoading) {
          dispatch(setLoading(false));
        }

        if (isAPISuccessful(data?.statusCode)) {
          if (!options?.keepLoading) {
            showAlert(1, "Labels added successfully!");
          }
          if (callback) {
            callback(data?.data);
          }
        } else {
          if (!options?.keepLoading) {
            showAlert(1, "Labels added successfully!");
          }
          if (callback) {
            callback({ url: "" });
          }
        }
      },
      (error: any) => {
        if (!options?.keepLoading) {
          dispatch(setLoading(false));
          showAlert(1, "Labels added successfully!");
        }
        if (callback) {
          callback({ url: "" });
        }
      },
    );
  };
};

export const getRatingListing =
  (batchId: string) => (dispatch: Dispatch<any>, getState: () => RootState) => {
    if (!navigator.onLine) {
      showAlert(
        2,
        "We're experiencing technical difficulties. Please try again later.",
      );
      return;
    }

    const {
      ratingListingQueryParameters: { pageNo, limit },
      ratingFilter: { sortBy, sortCriteria, filterType },
    } = getState().batches;

    const params = [
      `?pageNo=${pageNo}`,
      `limit=${limit}`,
      `batchId=${batchId}`,
      sortBy !== 0 ? `sortBy=${sortBy}` : "",
      sortCriteria.length !== 0 ? `sortCriteria=${sortCriteria}` : "",
      filterType ? `filterType=${filterType}` : "",
    ]
      .filter((param) => param) // Filter out empty strings
      .join("&"); // Join with '&'

    dispatch(setLoading(true));

    getApiCall(
      EndPoint.ratings,
      params,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data.statusCode)) {
          dispatch(setRatingListingData(data?.data));
        } else if (data.error)
          showAlert(
            2,
            "We're experiencing technical difficulties. Please try again later.",
          );
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const getRatingDetails =
  (ratingId: string) => (dispatch: Dispatch<any>) => {
    if (!navigator.onLine) {
      showAlert(
        2,
        "We're experiencing technical difficulties. Please try again later.",
      );
      return;
    }

    dispatch(setLoading(true));

    getApiCall(
      `${EndPoint.ratings}/${ratingId}`,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;

        if (isAPISuccessful(data?.statusCode)) {
          dispatch(setRatingBatchDetailsProps(data?.data)); // store API data in Redux
        } else if (data?.error) {
          showAlert(2, data?.message);
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const onProductTokenizationIpfsUpload =
  (
    values: {
      growerNote: string;
      productImage: File | null;
      batchMongoId: string;
      totalProductCount: string;
      operationType?: string;
      useStrainImage?: boolean;
      useStrainImagePlaceholder?: boolean;
      strainImageUrl?: string;
    },
    callback?: (metaDataIpfsHash: string, batchTokenId: string) => void,
  ) =>
  (dispatch: Dispatch<any>) => {
    if (!checkInternetAndShowAlert(showAlert)) return;

    dispatch(setLoading(true));

    const formData = new FormData();

    // Required fields
    formData.append("growerNote", values.growerNote);
    formData.append("batchMongoId", values.batchMongoId);
    formData.append("totalProductCount", values.totalProductCount);
    formData.append("useStrainImage", values.useStrainImage ? "true" : "false");
    if (values.useStrainImagePlaceholder) {
      formData.append("useStrainImagePlaceholder", "true");
    }
    if (values.strainImageUrl) {
      formData.append("strainImageUrl", values.strainImageUrl);
    }
    if (values.operationType) {
      formData.append("operationType", values.operationType);
    }

    // File field — must be appended as File/Blob
    if (values.productImage) {
      formData.append("productImage", values.productImage);
    }

    postApiCall(
      Module.manufacturer + EndPoint.productTokenizationIPFSUpload,
      formData,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          const metaDataIpfsHash = data?.data?.productMetadataIpfsHash;
          const batchTokenId = data?.data?.batchTokenId;

          callback?.(metaDataIpfsHash, batchTokenId);
        } else {
          dispatch(setLoading(false));
        }
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const onRefreshBatchListing =
  (callback: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));
    getApiCall(
      Module.manufacturer + EndPoint.refresBatch,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          showAlert(1, data?.message);
          callback();
        } else dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const getBatchFilterListing = () => (dispatch: Dispatch<any>) => {
  if (!navigator.onLine) {
    showAlert(
      2,
      "We're experiencing technical difficulties. Please try again later.",
    );
    return;
  }

  dispatch(setLoading(true));

  getApiCall(
    Module.manufacturer + EndPoint.batchesFilter,

    "",
    (respData: AxiosResponse<any>) => {
      const { data } = respData;

      if (isAPISuccessful(data?.statusCode)) {
        dispatch(setStrainListing(data?.data?.strains));
        dispatch(setSubTypeListing(data?.data?.subTypes));
        dispatch(setProductTypeListing(data?.data?.types));
      } else if (data?.error) showAlert(2, data?.message);

      dispatch(setLoading(false));
    },
    (error: any) => {
      dispatch(setLoading(false));
      showAlert(2, error?.message);
    },
  );
};

export const onArchiveNonTokenizedBatches = (
  isArchived: boolean,
  batchIndexId: string,
  callback?: Function | any,
) => {
  return (dispatch: any) => {
    dispatch(setLoading(true));

    putApiCall(
      `${Module.manufacturer}${EndPoint.batches}/${batchIndexId}${EndPoint.archiveNonTokenizedBatches}/${isArchived}`,
      {},
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          showAlert(1, data.message);

          if (callback) {
            callback();
          }
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        showAlert(2, error.message);
        dispatch(setLoading(false));
      },
    );
  };
};

export const validateDamagedSerialNumber =
  (
    damageSerialNumber: string,
    callback?: (damageSerialNumberExist: boolean) => void,
  ) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    getApiCall(
      `${Module.manufacturer}${EndPoint.productTokenization}/${damageSerialNumber}`,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data.statusCode === apiSuccessCode.success) {
          const isSerialNumberNotValid = data?.data?.damageSerialNumberExist;
          if (callback) callback(isSerialNumberNotValid);
        } else if (data.error) showAlert(2, data.message);
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const getBlockChainDetails =
  (batchIndexId: string) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    getApiCall(
      `${Module.manufacturer}${EndPoint.blockChainDetails}?batchIndexId=${batchIndexId}`,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        dispatch(setBlockChainDetails(data.data));
        if (data.statusCode === apiSuccessCode.success) {
        } else if (data.error) showAlert(2, data.message);
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const getConsumerListing =
  (batchIndexId: string) =>
  (dispatch: Dispatch<any>, getState: () => RootState) => {
    if (!navigator.onLine) {
      showAlert(
        2,
        "We're experiencing technical difficulties. Please try again later.",
      );
      return;
    }

    const {
      consumerListingQueryParameters: { pageNo, limit },
      consumerFilter: { sortBy, sortCriteria, reviewSubmitted },
    } = getState().batches;

    const params = [
      `pageNo=${pageNo}`,
      `limit=${limit}`,
      sortBy !== 0 ? `sortBy=${sortBy}` : "",
      sortCriteria.length !== 0 ? `sortCriteria=${sortCriteria}` : "",
      reviewSubmitted !== undefined ? `reviewSubmitted=${reviewSubmitted}` : "",
    ]
      .filter((param) => param) // Filter out empty strings
      .join("&"); // Join with '&'

    dispatch(setLoading(true));
    getApiCall(
      `${Module.manufacturer}${EndPoint.batches}/${batchIndexId}/consumers?${params}`,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data.statusCode)) {
          dispatch(setConsumerListingData(data?.data));
        } else if (data.error)
          showAlert(
            2,
            "We're experiencing technical difficulties. Please try again later.",
          );
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const getConsumerDetails =
  (consumerId: string) => (dispatch: Dispatch<any>) => {
    if (!navigator.onLine) {
      showAlert(
        2,
        "We're experiencing technical difficulties. Please try again later.",
      );
      return;
    }

    dispatch(setLoading(true));
    getApiCall(
      `${Module.manufacturer}${EndPoint.batches}/consumers/${consumerId}`,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data.statusCode)) {
          dispatch(setConsumerBatchDetails(data?.data));
        } else if (data.error)
          showAlert(
            2,
            "We're experiencing technical difficulties. Please try again later.",
          );
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const getProductCollectedListing =
  (consumerId: string) =>
  (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    const {
      productCollectedListingQueryParameters: { pageNo, limit },
    } = getState().batches;

    const params = `?consumerId=${consumerId}&pageNo=${pageNo}&limit=${limit}`;

    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer,
      `${EndPoint.productCollected}${params}`,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          dispatch(setProductCollectedListing(data?.data));
        } else if (data.error)
          showAlert(
            2,
            "We're experiencing technical difficulties. Please try again later.",
          );
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const onDownloadLabelCSV =
  (
    batchId: string,
    callback?: () => void,
    qrCodeId?: string,
    options?: { keepLoading?: boolean },
  ) =>
  (dispatch: Dispatch<any>) => {
    if (!navigator.onLine) {
      showAlert(
        2,
        "We're experiencing technical difficulties. Please try again later.",
      );
      return;
    }

    const timeZone = getTimezone();

    const queryParams = new URLSearchParams();

    if (batchId) {
      queryParams.append("batchId", batchId);
    }

    if (qrCodeId) {
      queryParams.append("qrCodeId", qrCodeId);
    }
    queryParams.append("timezone", timeZone);

    const params = `?${queryParams.toString()}`;

    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer + EndPoint.downloadLabelCSV,
      params,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data) {
          // Download CSV file
          if (data.data.url !== "") {
            const csvUrl = `${Env.IMAGE}${data.data.url}`;
            // setStrainFilter(csvUrl)
            fetch(csvUrl)
              .then((response) => response.blob())
              .then((blob) => {
                const fileURL = window.URL.createObjectURL(blob);
                const csvLink = document.createElement("a");
                csvLink.href = fileURL;
                csvLink.download = data.data.url.split("/").pop();
                document.body.appendChild(csvLink);
                csvLink.click();
                document.body.removeChild(csvLink);
                window.URL.revokeObjectURL(fileURL);
                if (callback) callback();
                if (!options?.keepLoading) {
                  dispatch(setLoading(false));
                }
              })
              .catch((error) => {
                showAlert(2, "Failed to download the CSV file.");
                dispatch(setLoading(false));
              });
          } else {
            showAlert(
              2,
              "Serial Number is not available for download. Please try again later.",
            );
            dispatch(setLoading(false));
          }
        } else {
          showAlert(
            2,
            "We're experiencing technical difficulties. Please try again later.",
          );
          dispatch(setLoading(false));
        }
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };
